import React, { useEffect, useMemo, useState } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { specLabel } from "@/lib/specialisms";
import { Plus, Upload, Trash2, Download, UserPlus, X, Film, Play, Pencil, Save, Image as ImageIcon, Eye, ListPlus } from "lucide-react";

const STATUS_STYLES = {
    open: "bg-yc-cream",
    "in-progress": "bg-yc-blue text-white",
    delivered: "bg-yc-pink text-white",
    complete: "bg-yc-ink text-white",
    draft: "bg-white",
};

const CreateModal = ({ open, onClose, brands, onCreated }) => {
    const [form, setForm] = useState({ brand_id: "", title: "", brief: "", requirements: "", videos_count: 1 });
    const [saving, setSaving] = useState(false);
    if (!open) return null;
    const submit = async () => {
        if (!form.brand_id || !form.title.trim()) { toast.error("Pick a brand and add a title"); return; }
        setSaving(true);
        try {
            await api.post("/admin/campaigns", { ...form, videos_count: parseInt(form.videos_count) || 1 });
            toast.success("Campaign created");
            onClose();
            setForm({ brand_id: "", title: "", brief: "", requirements: "", videos_count: 1 });
            onCreated();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not create campaign");
        } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-yc-ink/60 flex items-center justify-center p-4" data-testid="admin-create-campaign-modal">
            <div className="yc-card p-6 sm:p-8 bg-yc-cream w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
                <button onClick={onClose} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
                <h3 className="font-heading text-2xl font-black tracking-tighter">Create campaign for a brand</h3>
                <div className="space-y-4 mt-5">
                    <div>
                        <label className="yc-overline">Brand *</label>
                        <select data-testid="create-campaign-brand" className="yc-input mt-2" value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })}>
                            <option value="">Select a brand…</option>
                            {brands.map((b) => (
                                <option key={b.brand_id} value={b.brand_id}>{b.company_name}{b.email ? ` — ${b.email}` : ""}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="yc-overline">Title *</label>
                        <input data-testid="create-campaign-title" className="yc-input mt-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    </div>
                    <div>
                        <label className="yc-overline">Brief</label>
                        <textarea data-testid="create-campaign-brief" rows={3} className="yc-input mt-2" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} />
                    </div>
                    <div>
                        <label className="yc-overline">Requirements</label>
                        <textarea data-testid="create-campaign-reqs" rows={2} className="yc-input mt-2" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} />
                    </div>
                    <div>
                        <label className="yc-overline">Videos</label>
                        <input data-testid="create-campaign-videos" type="number" min={1} max={50} className="yc-input mt-2" value={form.videos_count} onChange={(e) => setForm({ ...form, videos_count: e.target.value })} />
                    </div>
                </div>
                <button data-testid="create-campaign-submit" disabled={saving} onClick={submit} className="yc-btn-primary w-full mt-6 disabled:opacity-50">{saving ? "Creating…" : "Create campaign"}</button>
            </div>
        </div>
    );
};

const AdminCampaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [brands, setBrands] = useState([]);
    const [creators, setCreators] = useState([]);
    const [selected, setSelected] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [creatorQuery, setCreatorQuery] = useState("");
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewIsVideo, setPreviewIsVideo] = useState(true);
    const [svcForm, setSvcForm] = useState({ description: "", price: "" });
    const [editing, setEditing] = useState(false);
    const [eform, setEform] = useState({});
    const [savingCampaign, setSavingCampaign] = useState(false);

    const startEdit = () => {
        setEform({
            title: selected.title || "", brief: selected.brief || "", requirements: selected.requirements || "",
            videos_count: selected.videos_count || 1, status: selected.status || "open",
        });
        setEditing(true);
    };
    const saveEdit = async () => {
        setSavingCampaign(true);
        try {
            await api.put(`/admin/campaigns/${selected.campaign_id}`, { ...eform, videos_count: parseInt(eform.videos_count) || 1 });
            toast.success("Campaign updated");
            setEditing(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save campaign");
        } finally { setSavingCampaign(false); }
    };

    const load = async () => {
        const r = await api.get("/admin/campaigns");
        setCampaigns(r.data);
        if (selected) {
            const fresh = r.data.find((c) => c.campaign_id === selected.campaign_id);
            if (fresh) setSelected(fresh);
        }
    };
    useEffect(() => {
        load();
        api.get("/admin/brands").then((r) => setBrands(r.data)).catch(() => {});
        api.get("/admin/creators").then((r) => setCreators((r.data || []).filter((c) => c.name))).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredCreators = useMemo(() => {
        const q = creatorQuery.trim().toLowerCase();
        const list = q ? creators.filter((c) => (c.name || "").toLowerCase().includes(q)) : creators;
        return list.slice(0, 50);
    }, [creators, creatorQuery]);

    const assign = async (creator_id) => {
        try {
            await api.post(`/admin/campaigns/${selected.campaign_id}/assign`, { creator_id });
            toast.success("Creator assigned");
            setCreatorQuery("");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not assign creator");
        }
    };

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/admin/campaigns/${selected.campaign_id}/asset/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const removeAsset = async (asset_id) => {
        if (!window.confirm("Remove this file?")) return;
        await api.delete(`/admin/campaigns/${selected.campaign_id}/asset/${asset_id}`);
        toast.success("Removed");
        await load();
    };

    const fetchBlob = async (a) => {
        const res = await api.get(`/admin/campaigns/${selected.campaign_id}/asset/${a.asset_id}/download`, { responseType: "blob" });
        return URL.createObjectURL(new Blob([res.data], { type: a.content_type || "application/octet-stream" }));
    };
    const openPreview = async (a) => {
        try { setPreviewUrl(await fetchBlob(a)); setPreviewIsVideo(a.kind === "video"); }
        catch { toast.error("Could not load file"); }
    };
    const download = async (a) => {
        try {
            const url = await fetchBlob(a);
            const link = document.createElement("a");
            link.href = url; link.download = a.original_filename; link.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Could not download file"); }
    };

    const addService = async () => {
        if (!svcForm.description.trim()) { toast.error("Add a description"); return; }
        try {
            await api.post(`/admin/campaigns/${selected.campaign_id}/service`, svcForm);
            toast.success("Service added");
            setSvcForm({ description: "", price: "" });
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not add service"); }
    };
    const removeService = async (service_id) => {
        await api.delete(`/admin/campaigns/${selected.campaign_id}/service/${service_id}`);
        toast.success("Removed");
        await load();
    };

    const videos = (selected?.assets || []).filter((a) => a.kind === "video");
    const photos = (selected?.assets || []).filter((a) => a.kind === "photo");
    const services = selected?.services || [];

    return (
        <DashboardShell role="admin" title="Campaigns" subtitle="Every campaign across all brands"
            actions={<button data-testid="admin-new-campaign-btn" onClick={() => setCreateOpen(true)} className="yc-btn-primary text-sm py-2 px-4"><Plus className="w-4 h-4 mr-2" /> New campaign</button>}>
            <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} brands={brands} onCreated={load} />
            {previewUrl && (
                <div className="fixed inset-0 z-50 bg-yc-ink/80 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }}>
                    <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
                        <button data-testid="close-preview" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }} className="absolute -top-10 right-0 text-white"><X className="w-6 h-6" /></button>
                        {previewIsVideo
                            ? <video src={previewUrl} controls autoPlay className="w-full rounded-2xl border-2 border-white" data-testid="video-preview" />
                            : <img src={previewUrl} alt="preview" className="w-full max-h-[80vh] object-contain rounded-2xl border-2 border-white" data-testid="photo-preview" />}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Campaign list */}
                <div className="lg:col-span-1">
                    <div className="yc-card-sm p-4">
                        <h3 className="font-heading text-lg font-bold">All campaigns ({campaigns.length})</h3>
                        <div className="mt-4 space-y-2 max-h-[70vh] overflow-y-auto">
                            {campaigns.map((c) => (
                                <button key={c.campaign_id} data-testid={`admin-camp-${c.campaign_id}`} onClick={() => { setSelected(c); setCreatorQuery(""); setEditing(false); }}
                                    className={`w-full text-left p-3 rounded-xl border-2 border-yc-ink transition-all ${selected?.campaign_id === c.campaign_id ? "bg-yc-blue text-white" : "bg-white"}`}>
                                    <div className="font-semibold truncate">{c.title}</div>
                                    <div className={`text-xs mt-0.5 ${selected?.campaign_id === c.campaign_id ? "text-white/80" : "text-yc-ink/60"}`}>{c.brand_company}</div>
                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                        <span className={`yc-badge text-[10px] ${selected?.campaign_id === c.campaign_id ? "bg-white text-yc-ink" : STATUS_STYLES[c.status] || "bg-yc-cream"}`}>{c.status}</span>
                                        {c.assigned_creator_name
                                            ? <span className={`text-[10px] ${selected?.campaign_id === c.campaign_id ? "text-white/80" : "text-yc-ink/60"}`}>· {c.assigned_creator_name}</span>
                                            : <span className="text-[10px] text-yc-pink font-semibold">· Unassigned</span>}
                                        {c.video_count > 0 && <span className={`text-[10px] flex items-center gap-0.5 ${selected?.campaign_id === c.campaign_id ? "text-white/80" : "text-yc-ink/60"}`}><Film className="w-3 h-3" />{c.video_count}</span>}
                                    </div>
                                </button>
                            ))}
                            {campaigns.length === 0 && <div className="text-center text-yc-ink/60 py-6 text-sm">No campaigns yet.</div>}
                        </div>
                    </div>
                </div>

                {/* Detail */}
                <div className="lg:col-span-2 space-y-5">
                    {!selected && <div className="yc-card p-10 text-center text-yc-ink/60">Select a campaign to manage it, or create a new one.</div>}
                    {selected && (
                        <>
                            <div className="yc-card p-6" data-testid="admin-camp-detail">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="yc-overline">{selected.brand_company}</div>
                                        <h2 className="font-heading text-2xl font-black tracking-tighter mt-1">{selected.title}</h2>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`yc-badge ${STATUS_STYLES[selected.status] || "bg-yc-cream"}`}>{selected.status}</span>
                                        {!editing && <button data-testid="edit-campaign-btn" onClick={startEdit} className="yc-btn-secondary text-xs py-1.5 px-3"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</button>}
                                    </div>
                                </div>
                                {editing ? (
                                    <div className="space-y-3 mt-4" data-testid="campaign-edit-form">
                                        <div>
                                            <label className="yc-overline">Title</label>
                                            <input data-testid="edit-campaign-title" className="yc-input mt-1" value={eform.title} onChange={(e) => setEform({ ...eform, title: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="yc-overline">Brief</label>
                                            <textarea data-testid="edit-campaign-brief" rows={3} className="yc-input mt-1" value={eform.brief} onChange={(e) => setEform({ ...eform, brief: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="yc-overline">Requirements</label>
                                            <textarea data-testid="edit-campaign-reqs" rows={2} className="yc-input mt-1" value={eform.requirements} onChange={(e) => setEform({ ...eform, requirements: e.target.value })} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="yc-overline">Videos</label>
                                                <input data-testid="edit-campaign-videos" type="number" min={1} max={50} className="yc-input mt-1" value={eform.videos_count} onChange={(e) => setEform({ ...eform, videos_count: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="yc-overline">Status</label>
                                                <select data-testid="edit-campaign-status" className="yc-input mt-1" value={eform.status} onChange={(e) => setEform({ ...eform, status: e.target.value })}>
                                                    {["draft", "open", "matched", "in-progress", "delivered", "complete"].map((s) => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex gap-2 pt-1">
                                            <button data-testid="save-campaign-btn" onClick={saveEdit} disabled={savingCampaign} className="yc-btn-primary text-sm py-2 px-4 disabled:opacity-50"><Save className="w-4 h-4 mr-2" /> {savingCampaign ? "Saving…" : "Save"}</button>
                                            <button onClick={() => setEditing(false)} className="yc-btn-secondary text-sm py-2 px-4"><X className="w-4 h-4 mr-2" /> Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {selected.brief && <p className="text-sm text-yc-ink/80 mt-3">{selected.brief}</p>}
                                        {selected.requirements && <p className="text-xs text-yc-ink/60 mt-2">{selected.requirements}</p>}
                                        <div className="text-xs text-yc-ink/60 mt-3">{selected.videos_count} video(s){selected.created_by_admin ? " · created by admin (free)" : ""}</div>
                                    </>
                                )}
                            </div>

                            {/* Assign / reassign creator */}
                            <div className="yc-card p-6" data-testid="admin-assign-panel">
                                <div className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-yc-blue" /><h3 className="font-heading text-lg font-bold">Assigned creator</h3></div>
                                {selected.assigned_creator_name
                                    ? <p className="text-sm mt-2">Currently: <strong data-testid="assigned-creator-name">{selected.assigned_creator_name}</strong>. Choose another below to reassign.</p>
                                    : <p className="text-sm mt-2 text-yc-pink font-semibold">No creator assigned yet.</p>}
                                <input data-testid="creator-search" className="yc-input mt-3" placeholder="Search creators by name…" value={creatorQuery} onChange={(e) => setCreatorQuery(e.target.value)} />
                                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                                    {filteredCreators.map((c) => (
                                        <div key={c.creator_id} className="flex items-center justify-between border-2 border-yc-ink rounded-xl p-3 bg-white">
                                            <div>
                                                <div className="font-semibold text-sm">{c.name}</div>
                                                <div className="text-xs text-yc-ink/60">{(c.specialisms || []).map(specLabel).join(", ") || "—"}</div>
                                            </div>
                                            <button data-testid={`assign-${c.creator_id}`} onClick={() => assign(c.creator_id)} disabled={c.creator_id === selected.assigned_creator_id}
                                                className="yc-btn-secondary text-xs py-1.5 px-3 disabled:opacity-40">{c.creator_id === selected.assigned_creator_id ? "Assigned" : "Assign"}</button>
                                        </div>
                                    ))}
                                    {filteredCreators.length === 0 && <div className="text-sm text-yc-ink/60 py-3">No creators found.</div>}
                                </div>
                            </div>

                            {/* Videos */}
                            <div className="yc-card p-6" data-testid="admin-videos-panel">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-2"><Film className="w-5 h-5 text-yc-pink" /><h3 className="font-heading text-lg font-bold">Videos ({videos.length})</h3></div>
                                    <label className={`yc-btn-primary text-sm py-2 px-4 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-video-label">
                                        <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : "Upload video"}
                                        <input type="file" accept="video/*" className="hidden" data-testid="upload-video-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
                                    </label>
                                </div>
                                <p className="text-xs text-yc-ink/60 mt-2">Uploaded videos are visible to the brand on their campaign.</p>
                                <div className="mt-4 space-y-2">
                                    {videos.map((a) => (
                                        <div key={a.asset_id} data-testid={`video-row-${a.asset_id}`} className="flex items-center justify-between gap-3 border-2 border-yc-ink rounded-xl p-3 bg-white">
                                            <div className="flex items-center gap-2 min-w-0"><Film className="w-4 h-4 shrink-0 text-yc-ink/50" /><span className="text-sm truncate">{a.original_filename}</span></div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button onClick={() => openPreview(a)} data-testid={`watch-${a.asset_id}`} className="p-2 rounded-lg hover:bg-yc-cream" title="Watch"><Play className="w-4 h-4" /></button>
                                                <button onClick={() => download(a)} data-testid={`download-${a.asset_id}`} className="p-2 rounded-lg hover:bg-yc-cream" title="Download"><Download className="w-4 h-4" /></button>
                                                <button onClick={() => removeAsset(a.asset_id)} data-testid={`remove-${a.asset_id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
                                            </div>
                                        </div>
                                    ))}
                                    {videos.length === 0 && <div className="text-sm text-yc-ink/60 py-3">No videos uploaded yet.</div>}
                                </div>
                            </div>

                            {/* Photos (photography asset drop) */}
                            <div className="yc-card p-6" data-testid="admin-photos-panel">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-2"><ImageIcon className="w-5 h-5 text-yc-blue" /><h3 className="font-heading text-lg font-bold">Photography ({photos.length})</h3></div>
                                    <label className={`yc-btn-primary text-sm py-2 px-4 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-photo-label">
                                        <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : "Upload photo"}
                                        <input type="file" accept="image/*" className="hidden" data-testid="upload-photo-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
                                    </label>
                                </div>
                                <p className="text-xs text-yc-ink/60 mt-2">Photography is visible to the brand to view & download on their campaign.</p>
                                <div className="mt-4 space-y-2">
                                    {photos.map((a) => (
                                        <div key={a.asset_id} data-testid={`photo-row-${a.asset_id}`} className="flex items-center justify-between gap-3 border-2 border-yc-ink rounded-xl p-3 bg-white">
                                            <div className="flex items-center gap-2 min-w-0"><ImageIcon className="w-4 h-4 shrink-0 text-yc-ink/50" /><span className="text-sm truncate">{a.original_filename}</span></div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button onClick={() => openPreview(a)} data-testid={`view-photo-${a.asset_id}`} className="p-2 rounded-lg hover:bg-yc-cream" title="View"><Eye className="w-4 h-4" /></button>
                                                <button onClick={() => download(a)} data-testid={`download-photo-${a.asset_id}`} className="p-2 rounded-lg hover:bg-yc-cream" title="Download"><Download className="w-4 h-4" /></button>
                                                <button onClick={() => removeAsset(a.asset_id)} data-testid={`remove-photo-${a.asset_id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
                                            </div>
                                        </div>
                                    ))}
                                    {photos.length === 0 && <div className="text-sm text-yc-ink/60 py-3">No photography uploaded yet.</div>}
                                </div>
                            </div>

                            {/* Service line items */}
                            <div className="yc-card p-6" data-testid="admin-services-panel">
                                <div className="flex items-center gap-2"><ListPlus className="w-5 h-5 text-yc-pink" /><h3 className="font-heading text-lg font-bold">Services ({services.length})</h3></div>
                                <p className="text-xs text-yc-ink/60 mt-2">Free-text line items (e.g. "Photography — half-day shoot"). Visible to the brand.</p>
                                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                                    <input data-testid="service-desc-input" className="yc-input flex-1" placeholder="Service description" value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} />
                                    <input data-testid="service-price-input" className="yc-input sm:w-40" placeholder="Price (e.g. £650)" value={svcForm.price} onChange={(e) => setSvcForm({ ...svcForm, price: e.target.value })} />
                                    <button data-testid="add-service-btn" onClick={addService} className="yc-btn-primary text-sm py-2 px-4 whitespace-nowrap"><Plus className="w-4 h-4 mr-1" /> Add</button>
                                </div>
                                <div className="mt-4 space-y-2">
                                    {services.map((s) => (
                                        <div key={s.service_id} data-testid={`service-row-${s.service_id}`} className="flex items-center justify-between gap-3 border-2 border-yc-ink rounded-xl p-3 bg-white">
                                            <div className="min-w-0"><span className="text-sm font-semibold">{s.description}</span>{s.price && <span className="text-sm text-yc-blue font-bold ml-2">{s.price}</span>}</div>
                                            <button onClick={() => removeService(s.service_id)} data-testid={`remove-service-${s.service_id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600 shrink-0" title="Remove"><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    ))}
                                    {services.length === 0 && <div className="text-sm text-yc-ink/60 py-3">No services added yet.</div>}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </DashboardShell>
    );
};

export default AdminCampaigns;
